import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { EmployeeLegalEntityDocument } from 'generated/resources/graphql'

import { Employee } from 'modules/core/hooks/useCurrentSession'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import OwnerFieldComponent from 'modules/expenses/components/expenses/Form/OwnerField'
import { isDraftExpensable, isOwnExpensable } from 'modules/expenses/lib/expensables/expensable'

import withFieldPermissions, { WithFieldPermissionsProps } from '../../withFieldPermissions'

const FIELD_NAME = 'employee'

interface Props extends WithFieldPermissionsProps {
  currentEmployee: Employee
  showEmployeeLegalEntity: boolean
  onEmployeeIdChange: (employeeId: number | undefined) => void
  isAutomaticExpensable?: boolean
  hasReportOnBehalfFeature: boolean
  canCreateExpensesForOthers: boolean
}

const OwnerField = ({
  currentEmployee,
  showEmployeeLegalEntity,
  onEmployeeIdChange,
  expensable,
  isAutomaticExpensable = false,
  isFieldReadOnly,
  hasReportOnBehalfFeature,
  canCreateExpensesForOthers,
  asReviewer,
}: Props) => {
  const {
    control,
    formState: { errors },
  } = useFormContext()

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(
    expensable?.owner?.employeeName?.id
  )

  const { data: employeeLegalEntityData } = useQuery(EmployeeLegalEntityDocument, {
    fetchPolicy: 'cache-first',
    variables: { employeeId: selectedEmployeeId ?? -1 },
    skip: !selectedEmployeeId || !showEmployeeLegalEntity,
  })

  const ownerLegalEntityName = employeeLegalEntityData?.employees?.employee?.legalEntity?.legalName

  const { getValues } = useFormContext()
  const { company } = useCurrentSession()

  const isGroupExpense = !!getValues('group')?.id
  const hideDueGroupInMyExpenses = !asReviewer && isGroupExpense
  if (hideDueGroupInMyExpenses) return null

  const canReportOnBehalf = hasReportOnBehalfFeature && canCreateExpensesForOthers
  const isOtherEmployeeExpense = expensable && !isOwnExpensable(expensable, currentEmployee)
  const showEmployeeSelector = isOtherEmployeeExpense || canReportOnBehalf
  if (!showEmployeeSelector) return null

  const isReadOnlyDueToNotDraft = expensable && !isDraftExpensable(expensable)
  const isReadOnlyDueToPermissions = !canReportOnBehalf
  const isReadOnlyDueToAutomaticExpensable = isAutomaticExpensable
  const isReadOnlyDueToGroupId = Boolean(expensable?.groupId)
  const companyLegalEntities = company?.legalEntities || []
  const legalEntityIds = companyLegalEntities
    .filter((le) => le.country == expensable?.reporter?.legalEntity?.country)
    .map((le) => le.id)

  const isReadOnly =
    isFieldReadOnly ||
    isReadOnlyDueToNotDraft ||
    isReadOnlyDueToPermissions ||
    isReadOnlyDueToAutomaticExpensable ||
    isReadOnlyDueToGroupId

  return (
    <Controller
      name={FIELD_NAME}
      control={control}
      defaultValue={currentEmployee.id}
      render={({ field }) => (
        <OwnerFieldComponent
          showEmployeeLegalEntity={showEmployeeLegalEntity}
          legalEntityIds={legalEntityIds}
          employeeId={selectedEmployeeId}
          ownerLegalEntityName={ownerLegalEntityName}
          errors={errors?.employee?.message ? [String(errors.employee.message)] : null}
          readonly={isReadOnly}
          onEmployeeIdSelected={(value) => {
            setSelectedEmployeeId(value ?? undefined)
            field.onChange(value)
            onEmployeeIdChange(value)
          }}
        />
      )}
    />
  )
}

export default withFieldPermissions(OwnerField, FIELD_NAME)
